"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BulkActionDialog = void 0;
const JuvareTheme_1 = require("@juvare/common-web-ui/JuvareTheme");
const utils_1 = require("@juvare/common-web-ui/utils");
const shared_1 = require("../../../../backend/features/bulk-actions/shared");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const Timelapse_1 = __importDefault(require("@mui/icons-material/Timelapse"));
const material_1 = require("@mui/material");
const react_1 = __importDefault(require("react"));
const BulkActionDialog = ({ storageClient, functionClient, functionResponsePromise, params, onClose, ariaLabelledById, ariaDescribedById, }) => {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const [bulkActionId, setBulkActionId] = react_1.default.useState();
    const [getResponse, setGetResponse] = react_1.default.useState();
    const [error, setError] = react_1.default.useState();
    react_1.default.useEffect(() => {
        let timeoutId = undefined;
        let disposed = false;
        const updateBulkActionProgress = () => {
            functionResponsePromise
                .then((functionResponse) => {
                setBulkActionId(functionResponse.result.id);
                timeoutId = window.setTimeout(() => {
                    storageClient
                        .get(functionResponse.result.id)
                        .then((getResponse) => {
                        setGetResponse(getResponse);
                        if (getResponse.data.status === 'pending' || getResponse.data.status === 'running') {
                            if (!disposed) {
                                window.setTimeout(updateBulkActionProgress, 1000);
                            }
                        }
                        else {
                            timeoutId = undefined;
                            if ((getResponse.data.status === 'finished' || getResponse.data.status === 'cancelled') &&
                                params.autoCloseWhenFinished) {
                                onClose({
                                    type: getResponse.data.type,
                                    status: getResponse.data.status,
                                    totalCount: getResponse.data.totalCount,
                                    processedCount: getResponse.data.processedCount,
                                    result: getResponse.data.result,
                                });
                            }
                        }
                    })
                        .catch((error) => {
                        setError(error);
                        timeoutId = undefined;
                    });
                }, 1000);
            })
                .catch(setError);
        };
        updateBulkActionProgress();
        return () => {
            disposed = true;
            if (timeoutId != null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [storageClient, functionResponsePromise, params.autoCloseWhenFinished, onClose]);
    const [cancelPressed, setCancelPressed] = react_1.default.useState(false);
    const handleCancel = () => {
        if (getResponse) {
            setCancelPressed(true);
            functionClient
                .executeFunction({
                functionName: shared_1.cancelBulkActionFunctionName,
                params: {
                    id: getResponse.id,
                },
            })
                .finally(() => undefined);
        }
    };
    const canCancel = (getResponse?.data.status === 'pending' || getResponse?.data.status === 'running') && !getResponse.data.cancel && !cancelPressed;
    let status = '';
    let progress = '';
    let totalCount = 0;
    let processedCount = 0;
    if (!bulkActionId) {
        status = t('bulkActions.createBulkAction');
    }
    else if (!getResponse) {
        status = t('bulkActions.gettingBulkAction');
    }
    else {
        status = t(`bulkActions.${getResponse.data.status}`);
        if (getResponse.data.totalCount != null) {
            totalCount = getResponse.data.totalCount;
            processedCount = getResponse.data.processedCount ?? 0;
            if (totalCount > 0) {
                progress = t('bulkActions.processedOfTotal', { processedCount, totalCount });
            }
        }
    }
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(material_1.DialogTitle, { id: ariaLabelledById }, params.title),
        react_1.default.createElement(material_1.DialogContent, { id: ariaDescribedById }, error || getResponse?.data.status === 'failed' ? (react_1.default.createElement(material_1.Alert, { severity: 'error' }, (0, utils_1.getErrorMessage)(error ?? getResponse?.data.errorMessage))) : (react_1.default.createElement(material_1.Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 } },
            react_1.default.createElement(material_1.Box, { sx: {
                    display: 'flex',
                    width: 40,
                    height: 40,
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: JuvareTheme_1.themeColors.icon.subtle,
                    backgroundColor: JuvareTheme_1.themeColors.alphaLightSurface.subtle,
                    borderRadius: 20,
                } },
                react_1.default.createElement(Timelapse_1.default, { color: 'inherit' })),
            react_1.default.createElement(material_1.Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 } },
                react_1.default.createElement(material_1.Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
                    react_1.default.createElement(material_1.Typography, { variant: 'subtitle2', sx: { color: JuvareTheme_1.themeColors.text.main } }, status),
                    progress && (react_1.default.createElement(material_1.Typography, { variant: 'body2', sx: { color: JuvareTheme_1.themeColors.text.subtle } }, progress))),
                react_1.default.createElement(material_1.LinearProgress, { variant: 'determinate', value: totalCount > 0 ? (processedCount * 100) / totalCount : 0 }))))),
        react_1.default.createElement(material_1.DialogActions, null,
            !error &&
                getResponse?.data.status !== 'finished' &&
                getResponse?.data.status !== 'failed' &&
                getResponse?.data.status !== 'cancelled' && (react_1.default.createElement(material_1.Button, { onClick: handleCancel, disabled: !canCancel }, t('common.cancel'))),
            (!params.autoCloseWhenFinished || !!error || getResponse?.data.status === 'failed') && (react_1.default.createElement(material_1.Button, { onClick: () => {
                    onClose({
                        type: getResponse?.data.type ?? params.type,
                        status: getResponse?.data.status ?? 'pending',
                        totalCount: getResponse?.data.totalCount,
                        processedCount: getResponse?.data.processedCount,
                        result: getResponse?.data.result,
                    });
                } }, t('common.ok'))))));
};
exports.BulkActionDialog = BulkActionDialog;
